import { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";
import { Button } from "./ui/button";

/**
 * "Installa l'app sul telefono" — avviso discreto, in fondo allo schermo.
 *
 * Compare SOLO quando l'installazione e' davvero possibile:
 *  - Android/Chrome/Edge/PC: il browser manda l'evento "beforeinstallprompt"
 *    e il pulsante apre la sua finestra di installazione;
 *  - iPhone/iPad (Safari): l'evento non esiste, quindi si spiega il gesto
 *    Condividi -> "Aggiungi a schermata Home".
 * Se l'app e' gia' installata, o se l'utente l'ha chiuso, non compare piu'.
 */
const CHIAVE = "insureassist-installa-chiuso";

export default function InstallApp() {
  const [evento, setEvento] = useState(null);
  const [iphone, setIphone] = useState(false);
  const [giaInstallata, setGiaInstallata] = useState(true);
  const [nascosto, setNascosto] = useState(true);

  useEffect(() => {
    try {
      setNascosto(window.localStorage.getItem(CHIAVE) === "1");
    } catch (e) {
      setNascosto(false);
    }

    const standalone =
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      window.navigator.standalone === true;
    setGiaInstallata(!!standalone);

    const ua = window.navigator.userAgent || "";
    const ios = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && "ontouchend" in document);
    setIphone(ios);

    const suProposta = (e) => {
      e.preventDefault();
      setEvento(e);
    };
    const suInstallata = () => {
      setGiaInstallata(true);
      setEvento(null);
    };
    window.addEventListener("beforeinstallprompt", suProposta);
    window.addEventListener("appinstalled", suInstallata);
    return () => {
      window.removeEventListener("beforeinstallprompt", suProposta);
      window.removeEventListener("appinstalled", suInstallata);
    };
  }, []);

  const chiudi = () => {
    setNascosto(true);
    try {
      window.localStorage.setItem(CHIAVE, "1");
    } catch (e) {
      /* niente memoria: si rivedra' al prossimo avvio, non e' un problema */
    }
  };

  const installa = async () => {
    if (!evento) return;
    evento.prompt();
    try {
      await evento.userChoice;
    } catch (e) {
      /* l'utente ha annullato */
    }
    setEvento(null);
  };

  if (giaInstallata || nascosto) return null;
  if (!evento && !iphone) return null;

  return (
    <div
      data-testid="install-app-avviso"
      className="fixed bottom-3 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-1.5rem)] max-w-md rounded-xl border border-border bg-card shadow-lg p-3 flex items-start gap-3"
    >
      <div className="h-9 w-9 shrink-0 rounded-lg bg-primary/10 flex items-center justify-center">
        <Download className="h-4 w-4 text-primary" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">Installa InsureAssist sul telefono</p>
        {evento ? (
          <p className="text-xs text-muted-foreground mt-0.5">
            Si apre come una vera app, a schermo intero, con la sua icona.
          </p>
        ) : (
          <p className="text-xs text-muted-foreground mt-0.5 flex flex-wrap items-center gap-1">
            Tocca <Share className="h-3.5 w-3.5 inline" /> Condividi in basso, poi
            <span className="inline-flex items-center gap-1 font-medium text-foreground">
              <Plus className="h-3.5 w-3.5" /> Aggiungi a schermata Home
            </span>
            .
          </p>
        )}
        <div className="flex items-center gap-2 mt-2">
          {evento ? (
            <Button size="sm" onClick={installa} data-testid="install-app-bottone">
              Installa
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" onClick={chiudi} data-testid="install-app-chiudi">
            Non ora
          </Button>
        </div>
      </div>
      <button
        type="button"
        onClick={chiudi}
        aria-label="Chiudi l'avviso di installazione"
        className="shrink-0 rounded p-1 text-muted-foreground hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
